import { Link, NavLink, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ChevronDown } from "lucide-react";
import { requireAuth, signOut, useAuth } from "@/lib/auth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const AccountNav = () => {
  const auth = useAuth();
  const nav = useNavigate();
  if (!auth) return <button className="min-h-[44px] whitespace-nowrap text-[0.75rem] tracking-wide text-[var(--charcoal)] underline-offset-4 hover:underline sm:text-[0.8rem]" onClick={() => requireAuth("Your account")} data-testid="nav-sign-in-button">Sign in</button>;
  const item = "min-h-[44px] cursor-pointer text-sm";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex min-h-[44px] max-w-[7rem] items-center gap-1 truncate text-[0.75rem] tracking-wide text-[var(--charcoal)] outline-none sm:max-w-none sm:text-[0.8rem]" data-testid="nav-account-button">
        {auth.user.name.split(" ")[0]} <ChevronDown size={13} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[13rem] rounded-xl border-[var(--line)] bg-[var(--bone)]" data-testid="account-menu">
        <DropdownMenuItem className={item} onSelect={() => nav("/properties")} data-testid="account-menu-my-properties">My properties</DropdownMenuItem>
        <DropdownMenuItem className={item} onSelect={() => nav("/owner")} data-testid="account-menu-owner-view">My places as owner</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className={item} onSelect={() => { signOut(); window.location.assign("/"); }} data-testid="account-menu-sign-out">Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const navCls = ({ isActive }) =>
  `whitespace-nowrap text-[0.75rem] sm:text-[0.8rem] tracking-wide min-h-[44px] inline-flex items-center transition-colors duration-200 ${
    isActive ? "text-[var(--charcoal)]" : "text-[var(--stone-dark)] hover:text-[var(--charcoal)]"
  }`;

export const SiteHeader = () => (
  <header
    data-testid="site-header"
    className="sticky top-0 z-50 border-b border-[var(--line)] bg-[rgba(250,247,242,0.85)] backdrop-blur-xl"
  >
    <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-5 sm:px-8 lg:px-12">
      <Link to="/" data-testid="header-wordmark" aria-label="PlaceBacker home">
        <Logo size={26} />
      </Link>
      <nav className="flex items-center gap-3 sm:gap-8">
        <NavLink to="/explore" className={navCls} data-testid="nav-explore-link">Explore Projects</NavLink>
        <NavLink to="/properties" className={navCls} data-testid="nav-my-properties-link"><span className="sm:hidden">Mine</span><span className="hidden sm:inline">My properties</span></NavLink>
        <AccountNav />
      </nav>
    </div>
  </header>
);

export const SiteFooter = () => (
  <footer className="mt-24 border-t border-[var(--line)]" data-testid="site-footer">
    <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-10 sm:flex-row sm:items-end sm:justify-between sm:px-8 lg:px-12">
      <div>
        <Logo size={24} />
        <div className="mt-1 text-sm text-[var(--stone-dark)]" data-testid="footer-tagline">Fund what a place could become.</div>
      </div>
      <p className="max-w-md text-xs leading-relaxed text-[var(--stone-dark)]">
        Visions are AI-generated concepts from your photos. They are not measured drawings, inspections or structural assessments — real repairs need qualified professionals.
      </p>
    </div>
  </footer>
);

export const GroundingBadge = ({ status = "Conceptual", testid = "grounding-badge" }) => (
  <span
    data-testid={testid}
    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--charcoal)]/30 bg-[var(--bone)]/80 px-3 py-1 font-mono-ui text-[0.65rem] uppercase tracking-[0.08em] text-[var(--charcoal)] backdrop-blur"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
    {status}
  </span>
);

export const SectionHead = ({ eyebrow, title, children, testid }) => (
  <div className="mb-6 sm:mb-8" data-testid={testid}>
    {eyebrow && <div className="eyebrow mb-3">{eyebrow}</div>}
    <h2 className="font-display text-3xl leading-tight tracking-tight sm:text-4xl">{title}</h2>
    {children && <div className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--ink-soft)] sm:text-base">{children}</div>}
  </div>
);
